/**
 * Instrument Specifications
 * Centralized configuration for contract values, tick sizes, and other instrument-specific data
 */

import { isVestFeedSymbol, isVestCryptoFeedSymbol, vestFeedLabel } from '../utils/vestFeed';

export type InstrumentType = 'futures' | 'crypto';

export interface InstrumentSpec {
  symbol: string;
  name: string;
  instrumentType: InstrumentType;
  pointValue: number;      // Dollar value per point
  tickSize: number;        // Minimum price increment
  tickValue: number;       // Dollar value per tick
  exchange: string;
  description: string;
}

export const INSTRUMENTS: Record<string, InstrumentSpec> = {
  // Micro Silver (COMEX)
  'SIL': {
    symbol: 'SIL',
    name: 'Micro Silver',
    instrumentType: 'futures',
    pointValue: 1000,       // $1000 per point (1,000 oz contract)
    tickSize: 0.005,        // Half a cent per oz
    tickValue: 5,           // $5 per tick
    exchange: 'COMEX',
    description: 'Micro Silver Futures (1,000 oz)'
  },

  // Micro E-mini Futures
  'MNQ': {
    symbol: 'MNQ',
    name: 'Micro E-mini Nasdaq-100',
    instrumentType: 'futures',
    pointValue: 2,          // $2 per point
    tickSize: 0.25,         // Quarter point
    tickValue: 0.50,        // $0.50 per tick (0.25 * $2)
    exchange: 'CME',
    description: 'Micro E-mini Nasdaq-100 Futures'
  },
  'MES': {
    symbol: 'MES',
    name: 'Micro E-mini S&P 500',
    instrumentType: 'futures',
    pointValue: 5,          // $5 per point
    tickSize: 0.25,         // Quarter point
    tickValue: 1.25,        // $1.25 per tick (0.25 * $5)
    exchange: 'CME',
    description: 'Micro E-mini S&P 500 Futures'
  },
  'MYM': {
    symbol: 'MYM',
    name: 'Micro E-mini Dow',
    instrumentType: 'futures',
    pointValue: 0.50,       // $0.50 per point
    tickSize: 1,            // 1 point
    tickValue: 0.50,        // $0.50 per tick
    exchange: 'CME',
    description: 'Micro E-mini Dow Futures'
  },
  'M2K': {
    symbol: 'M2K',
    name: 'Micro E-mini Russell 2000',
    instrumentType: 'futures',
    pointValue: 5,          // $5 per point
    tickSize: 0.10,         // 0.1 point
    tickValue: 0.50,        // $0.50 per tick (0.1 * $5)
    exchange: 'CME',
    description: 'Micro E-mini Russell 2000 Futures'
  },

  // E-mini Futures
  'NQ': {
    symbol: 'NQ',
    name: 'E-mini Nasdaq-100',
    instrumentType: 'futures',
    pointValue: 20,         // $20 per point
    tickSize: 0.25,         // Quarter point
    tickValue: 5,           // $5 per tick (0.25 * $20)
    exchange: 'CME',
    description: 'E-mini Nasdaq-100 Futures'
  },
  'ES': {
    symbol: 'ES',
    name: 'E-mini S&P 500',
    instrumentType: 'futures',
    pointValue: 50,         // $50 per point
    tickSize: 0.25,         // Quarter point
    tickValue: 12.50,       // $12.50 per tick (0.25 * $50)
    exchange: 'CME',
    description: 'E-mini S&P 500 Futures'
  },
  'YM': {
    symbol: 'YM',
    name: 'E-mini Dow',
    instrumentType: 'futures',
    pointValue: 5,          // $5 per point
    tickSize: 1,            // 1 point
    tickValue: 5,           // $5 per tick
    exchange: 'CME',
    description: 'E-mini Dow Futures'
  },
  'RTY': {
    symbol: 'RTY',
    name: 'E-mini Russell 2000',
    instrumentType: 'futures',
    pointValue: 50,         // $50 per point
    tickSize: 0.10,         // 0.1 point
    tickValue: 5,           // $5 per tick (0.1 * $50)
    exchange: 'CME',
    description: 'E-mini Russell 2000 Futures'
  },

  // Crypto
  'BTCUSD_PERP_BINANCE': {
    symbol: 'BTCUSD_PERP_BINANCE',
    name: 'Bitcoin',
    instrumentType: 'crypto',
    pointValue: 1,
    tickSize: 0.01,
    tickValue: 0.01,
    exchange: 'BINANCE',
    description: 'BTC/USD Perpetual (Binance)',
  },
  'ETHUSD-BITFINEX': {
    symbol: 'ETHUSD-BITFINEX',
    name: 'Ethereum',
    instrumentType: 'crypto',
    pointValue: 1,
    tickSize: 0.01,
    tickValue: 0.01,
    exchange: 'BITFINEX',
    description: 'ETH/USD (Bitfinex)',
  },
  'SOLUSDT-BMEX': {
    symbol: 'SOLUSDT-BMEX',
    name: 'Solana',
    instrumentType: 'crypto',
    pointValue: 1,
    tickSize: 0.001,
    tickValue: 0.001,
    exchange: 'BMEX',
    description: 'SOL/USDT (BitMEX)',
  },

  // Vest chart feed (#622): charts of Vest's tape, never tradeable here.
  // Tick 0.1 since Vest changed it on 2026-10-14 (was 1; #626).
  'BTC-PERP.VEST': {
    symbol: 'BTC-PERP.VEST',
    name: 'Bitcoin',
    instrumentType: 'crypto',
    pointValue: 1,
    tickSize: 0.1,
    tickValue: 0.1,
    exchange: 'VEST',
    description: 'BTC-PERP (Vest tape, chart only)',
  },
  // #626: tick = Vest exchangeInfo minTickSize (ETH 0.01, SOL 0.001).
  'ETH-PERP.VEST': {
    symbol: 'ETH-PERP.VEST',
    name: 'Ethereum',
    instrumentType: 'crypto',
    pointValue: 1,
    tickSize: 0.01,
    tickValue: 0.01,
    exchange: 'VEST',
    description: 'ETH-PERP (Vest tape, chart only)',
  },
  'SOL-PERP.VEST': {
    symbol: 'SOL-PERP.VEST',
    name: 'Solana',
    instrumentType: 'crypto',
    pointValue: 1,
    tickSize: 0.001,
    tickValue: 0.001,
    exchange: 'VEST',
    description: 'SOL-PERP (Vest tape, chart only)',
  },
};

/**
 * Get instrument specification by symbol
 * Strips suffix (e.g., MNQZ5 -> MNQ, MNQH6.CME -> MNQ) to find the base contract
 */
export function getInstrumentSpec(symbol: string): InstrumentSpec {
  // Guard against undefined/null/empty symbol
  if (!symbol) {
    console.warn('getInstrumentSpec called with empty symbol, using MNQ defaults');
    return INSTRUMENTS['MNQ'];
  }

  // First strip exchange suffix (e.g., .CME, .CBOT, etc.)
  const withoutExchange = symbol.replace(/\.(CME|CBOT|NYMEX|COMEX|NYBOT|KCBT|MGE|BINANCE|COINBASE|BITFINEX|BMEX)$/i, '');
  // Extract base symbol (remove contract month/year suffix)
  const baseSymbol = withoutExchange.replace(/[A-Z]\d+$/, '').replace(/\d{4}$/, '');

  // Try direct match first
  if (INSTRUMENTS[symbol]) {
    return INSTRUMENTS[symbol];
  }

  // Try base symbol match
  if (INSTRUMENTS[baseSymbol]) {
    return INSTRUMENTS[baseSymbol];
  }

  // A Vest chart feed symbol without its own entry must NEVER fall back to
  // MNQ (MNQ is tradeable): a chart-only spec instead.
  if (isVestFeedSymbol(symbol)) {
    return {
      symbol: symbol.toUpperCase(),
      name: vestFeedLabel(symbol),
      instrumentType: isVestCryptoFeedSymbol(symbol) ? 'crypto' : 'futures',
      pointValue: 1,
      tickSize: 0.01,
      tickValue: 0.01,
      exchange: 'VEST',
      description: `${vestFeedLabel(symbol)} (Vest tape, chart only)`,
    };
  }

  // Default fallback (assume MNQ if not found)
  console.warn(`Instrument spec not found for ${symbol}, using MNQ defaults`);
  return INSTRUMENTS['MNQ'];
}

export function isTradeable(spec: InstrumentSpec): boolean {
  // Vest chart feed specs are charts only, whatever their instrument type.
  return spec.instrumentType === 'futures' && spec.exchange !== 'VEST';
}

export function isAlwaysOpen(spec: InstrumentSpec): boolean {
  return spec.instrumentType === 'crypto';
}

export function hasSessionConcepts(spec: InstrumentSpec): boolean {
  return spec.instrumentType === 'futures';
}

export function getDefaultVolatilityThreshold(spec: InstrumentSpec): number {
  return spec.instrumentType === 'crypto' ? 100.0 : 12.0;
}

/**
 * Calculate dollar P&L from point P&L
 */
export function pointsToDollars(points: number, quantity: number, symbol: string): number {
  const spec = getInstrumentSpec(symbol);
  return points * Math.abs(quantity) * spec.pointValue;
}

/**
 * Calculate point P&L from price difference
 */
export function calculatePointPnL(entryPrice: number, exitPrice: number, isLong: boolean): number {
  return isLong ? exitPrice - entryPrice : entryPrice - exitPrice;
}

/**
 * Calculate complete P&L (points and dollars)
 */
export function calculatePnL(
  entryPrice: number,
  exitPrice: number,
  quantity: number,
  symbol: string
): { points: number; dollars: number } {
  const isLong = quantity > 0;
  const points = calculatePointPnL(entryPrice, exitPrice, isLong);
  const dollars = pointsToDollars(points, quantity, symbol);
  return { points, dollars };
}

/**
 * Round price to nearest tick multiple
 */
export function roundToTick(price: number, symbol: string): number {
  const spec = getInstrumentSpec(symbol);
  return Math.round(price / spec.tickSize) * spec.tickSize;
}

/**
 * Calculate how many additional contracts needed to move average price to target
 * Formula: additionalQty = currentQty * (currentAvg - targetAvg) / (targetAvg - entryPrice)
 *
 * @param currentAvg Current average price of position
 * @param currentQty Current position quantity (absolute value)
 * @param targetAvg Target average price we want to achieve (where you're dragging the DCA handle to)
 * @param entryPrice Price at which you'll be adding the new contracts (current market price)
 * @param isLong Whether this is a long position
 * @returns Number of contracts needed to add (rounded up to nearest integer), can be 0 if invalid
 */
export function calculateDCAQuantity(
  currentAvg: number,
  currentQty: number,
  targetAvg: number,
  currentPrice: number,
  _isLong: boolean
): number {
  // "Add N contracts to bring avg to [line]"
  // Line = target avg (between current price and current avg)
  // Contracts added at current market price
  // Q2 = Q1 * (currentAvg - targetAvg) / (targetAvg - currentPrice)

  const absQty = Math.abs(currentQty);
  if (absQty === 0) return 0;

  const numerator = absQty * (currentAvg - targetAvg);
  const denominator = targetAvg - currentPrice;
  if (Math.abs(denominator) < 0.01) return 0;

  const raw = numerator / denominator;
  if (raw <= 0 || !isFinite(raw)) return 0;

  return Math.ceil(raw);
}

